import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Star, MessageSquarePlus } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initial = { name: "", location: "", vehicle: "", rating: 5, quote: "" };

export default function LeaveReview() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const setRating = (n) => setForm((f) => ({ ...f, rating: n }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.quote || !form.rating) {
      toast.error("Please add your name, a rating, and your review.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews`, {
        name: form.name,
        location: form.location || null,
        vehicle: form.vehicle || null,
        rating: Number(form.rating),
        quote: form.quote,
      });
      toast.success("Thank you! Your review has been submitted.");
      setForm(initial);
      setSubmitted(true);
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Could not submit review.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="leave-review"
      data-testid="leave-review-section"
      className="relative py-24 lg:py-28 bg-[#050505]"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Left */}
          <div className="lg:col-span-5 reveal">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-8 bg-gold" />
              <span className="text-xs uppercase tracking-[0.32em] text-gold/90">
                Leave a Review
              </span>
            </div>
            <h2 className="font-display font-light text-5xl lg:text-6xl tracking-tight leading-[1.02]">
              Got your car detailed by us? Tell us
              <span className="italic gold-shimmer"> how it went</span>.
            </h2>
            <p className="mt-7 text-white/60 leading-relaxed max-w-md">
              Your honest feedback helps us improve and helps other Seattle
              drivers find us. We read every single review.
            </p>
          </div>

          {/* Right form */}
          <div className="lg:col-span-7 reveal">
            {submitted ? (
              <div
                data-testid="leave-review-success"
                className="relative bg-[#0a0a0a] border border-gold/30 p-10 lg:p-14 rounded-sm text-center"
              >
                <div className="mx-auto h-14 w-14 grid place-items-center rounded-full border border-gold/60 text-gold mb-6">
                  <MessageSquarePlus size={22} />
                </div>
                <h3 className="font-display text-3xl lg:text-4xl text-white">
                  Thank you!
                </h3>
                <p className="mt-4 text-white/65 max-w-md mx-auto">
                  Your review has been received. We&apos;ll look it over and
                  share it with future clients soon.
                </p>
                <button
                  type="button"
                  onClick={() => setSubmitted(false)}
                  data-testid="leave-review-again"
                  className="mt-8 inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-gold hover:text-[#F3E5AB]"
                >
                  Leave another review
                </button>
              </div>
            ) : (
              <form
                onSubmit={onSubmit}
                data-testid="leave-review-form"
                className="relative bg-[#0a0a0a] border border-white/10 p-8 lg:p-12 rounded-sm"
              >
                <div className="absolute -top-px left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/60 to-transparent" />

                {/* Star rating */}
                <div>
                  <Label className="text-xs uppercase tracking-[0.22em] text-white/60">
                    Your Rating
                    <span className="text-gold ml-1">*</span>
                  </Label>
                  <div className="mt-4 flex items-center gap-2">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button
                        key={n}
                        type="button"
                        onClick={() => setRating(n)}
                        data-testid={`leave-review-star-${n}`}
                        aria-label={`Rate ${n} stars`}
                        className="p-1 transition-transform hover:scale-110"
                      >
                        <Star
                          size={28}
                          strokeWidth={1.5}
                          className={
                            n <= form.rating
                              ? "text-gold fill-current"
                              : "text-white/25"
                          }
                          fill={n <= form.rating ? "currentColor" : "none"}
                        />
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <Field
                    label="First and last name"
                    name="name"
                    testid="leave-review-input-name"
                    value={form.name}
                    onChange={onChange}
                    required
                  />
                  <Field
                    label="Location / Neighborhood"
                    name="location"
                    testid="leave-review-input-location"
                    value={form.location}
                    onChange={onChange}
                    placeholder="e.g. Northgate"
                  />
                  <div className="sm:col-span-2">
                    <Field
                      label="Vehicle &amp; Service (optional)"
                      name="vehicle"
                      testid="leave-review-input-vehicle"
                      value={form.vehicle}
                      onChange={onChange}
                      placeholder="e.g. 2018 Subaru Outback · Interior Revival"
                    />
                  </div>
                </div>

                <div className="mt-6">
                  <Label
                    htmlFor="quote"
                    className="text-xs uppercase tracking-[0.22em] text-white/60"
                  >
                    Your Review
                    <span className="text-gold ml-1">*</span>
                  </Label>
                  <Textarea
                    id="quote"
                    name="quote"
                    data-testid="leave-review-input-quote"
                    value={form.quote}
                    onChange={onChange}
                    rows={5}
                    required
                    className="gold-input mt-3 bg-black/40 border-white/15 text-white placeholder:text-white/30 focus-visible:ring-0"
                    placeholder="Tell us about your experience..."
                  />
                </div>

                <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <p className="text-xs text-white/40 max-w-sm">
                    Reviews are checked before being shown on the site to
                    keep things genuine.
                  </p>
                  <Button
                    type="submit"
                    data-testid="leave-review-submit"
                    disabled={submitting}
                    className="bg-gold text-black hover:bg-[#F3E5AB] rounded-full px-8 py-6 uppercase tracking-[0.22em] text-xs font-medium"
                  >
                    {submitting ? "Sending..." : "Submit Review"}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, name, type = "text", testid, value, onChange, required, placeholder, autoComplete }) {
  return (
    <div>
      <Label
        htmlFor={name}
        className="text-xs uppercase tracking-[0.22em] text-white/60"
      >
        {label}
        {required && <span className="text-gold ml-1">*</span>}
      </Label>
      <Input
        id={name}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        autoComplete={autoComplete || "off"}
        data-testid={testid}
        className="gold-input mt-3 bg-black/40 border-white/15 text-white placeholder:text-white/30 focus-visible:ring-0 h-12"
      />
    </div>
  );
}
